import Vue from 'vue';
import axios from 'axios';
import userService from '@/modules/user-service';

const USER_REQUEST = 'USER_REQUEST',
	USER_SUCCESS = 'USER_SUCCESS',
	USER_ERROR = 'USER_ERROR',
	PASSWORD_CHANGE_REQUEST = 'PASSWORD_CHANGE_REQUEST',
	PASSWORD_CHANGE_SUCCESS = 'PASSWORD_CHANGE_SUCCESS',
	PASSWORD_CHANGE_ERROR = 'PASSWORD_CHANGE_ERROR',
	EMAIL_CHANGE_REQUEST = 'EMAIL_CHANGE_REQUEST',
	EMAIL_CHANGE_SUCCESS = 'EMAIL_CHANGE_SUCCESS',
	EMAIL_CHANGE_ERROR = 'EMAIL_CHANGE_ERROR',
	CONFIRM_EMAIL_REQUEST = 'CONFIRM_EMAIL_REQUEST',
	CONFIRM_EMAIL_SUCCESS = 'CONFIRM_EMAIL_SUCCESS',
	CONFIRM_EMAIL_ERROR = 'CONFIRM_EMAIL_ERROR',
	CREATE_TFA_REQUEST = 'CREATE_TFA_REQUEST',
	CREATE_TFA_SUCCESS = 'CREATE_TFA_SUCCESS',
	CREATE_TFA_ERROR = 'CREATE_TFA_ERROR',
	CREATE_ADDRESS_REQUEST = 'CREATE_ADDRESS_REQUEST',
	CREATE_ADDRESS_SUCCESS = 'CREATE_ADDRESS_SUCCESS',
	SET_ADDRESSES = 'SET_ADDRESSES',
	SET_EMAIL_VERIFIED = 'SET_EMAIL_VERIFIED',
	ENABLE_TFA_REQUEST = 'ENABLE_TFA_REQUEST',
	ENABLE_TFA_SUCCESS = 'ENABLE_TFA_SUCCESS',
	ENABLE_TFA_ERROR = 'ENABLE_TFA_ERROR',
	DISABLE_TFA_REQUEST = 'DISABLE_TFA_REQUEST',
	DISABLE_TFA_SUCCESS = 'DISABLE_TFA_SUCCESS',
	DISABLE_TFA_ERROR = 'DISABLE_TFA_ERROR',
	UPDATE_USER_REQUEST = 'UPDATE_USER_REQUEST',
	UPDATE_USER_SUCCESS = 'UPDATE_USER_SUCCESS',
	UPDATE_USER_ERROR = 'UPDATE_USER_ERROR',
	DELETE_REQUEST = 'DELETE_REQUEST',
	DELETE_SUCCESS = 'DELETE_SUCCESS',
	DELETE_ERROR = 'DELETE_ERROR',
	RESET_USER_STATUS = 'RESET_USER_STATUS',
	AUTH_LOGOUT = 'AUTH_LOGOUT',
	ACCOUNT_REQUEST = 'ACCOUNT_REQUEST',
	MIN_PASSWORD_LENGTH = 8,
	GET_EXPORT_STATUS_REQUEST = 'GET_EXPORT_STATUS_REQUEST',
	GET_EXPORT_STATUS_SUCCESS = 'GET_EXPORT_STATUS_SUCCESS',
	SET_EXPORT_FLAG = 'SET_EXPORT_FLAG';

const initialStatusState = {
	user: {
		info: {},
		update: {},
		password: {},
		email: {},
		delete: {},
		createTfa: {},
		enableTfa: {},
		disableTfa: {},
		confirmEmail: {}
	}
};

function formatErrors(reason) {
	return {
		authPasswordInvalid: reason.invalidCredentials || reason.type === 'UNAUTHORIZED' || reason.type === 'PASSWORD_REQUIRED',
		changePasswordRequired: reason.newPasswordRequired,
		changePasswordInvalid: reason.newPasswordShort,
		changeEmailInvalid: reason.type === 'INVALID_EMAIL',
		changeEmailTaken: reason.type === 'INVALID_EMAIL' && reason.message.toLowerCase().includes('already'),
		tfaNotEnabled: reason.type === 'MFA_NOT_ENABLED',
		tfaFirstCodeInvalid: reason.type === 'CODE_NOT_VALID' && reason.message.toLowerCase().includes('first'),
		tfaSecondCodeInvalid: reason.type === 'CODE_NOT_VALID' && reason.message.toLowerCase().includes('second'),
		cidrRequired: reason.type === 'CIDR_REQUIRED',
		cidrInvalid: reason.type === 'INVALID_CIDR'
	};
}

function resetStatusState () {
	return initialStatusState.user;
}

const state = {
	status: initialStatusState,
	user: {},
	tfa: {}
};

const getters = {
	user: state => state.user,
	token: state => state.user.token,
	roles: state => state.user.roles || {},
	address: state => state.user.address,
	isUserLoaded: state => !!state.user.email,
	userStatus: state => state.status.user.info || {},
	userStatusUpdate: state => state.status.user.update || {},
	userStatusEmail: state => state.status.user.email || {},
	userStatusConfirmEmail: state => state.status.user.confirmEmail || {},
	userStatusPassword: state => state.status.user.password || {},
	userStatusDelete: state => state.status.user.delete || {},
	userStatusCreateTfa: state => state.status.user.createTfa || {},
	userStatusEnableTfa: state => state.status.user.enableTfa || {},
	userStatusDisableTfa: state => state.status.user.disableTfa || {},
	userStatusEnableTracking: state => state.status.user.tracking || {},
	tfa: state => state.tfa,
	hasUsAddress: state => state.user.address && state.user.address.country === 'US',
	hasNonUsAddress: state => state.user.address && state.user.address.country !== 'US'
};

function success() {
	return {
		success: true
	};
}

function loading() {
	return {
		loading: true
	};
}

function error(error) {
	return {
		error: error && formatErrors(error) || true
	};
}

const actions = {
	[USER_REQUEST]: ({ commit, dispatch }) => {
		commit(USER_REQUEST);
		const jobs = [dispatch(ACCOUNT_REQUEST),userService.getUser()];
		return Promise.all(jobs)
			.then(response => {
				const user = response[1];
				commit(USER_SUCCESS, user);
				commit('ACCOUNT_SUCCESS', response[0]);
				// fire set address action to trigger get tax request
				if (user.address) {
					dispatch(SET_ADDRESSES, [user.address]);
				}
				// no drive claims could mean user is stuck in export control, check and see
				if (user.roles && !user.roles.tron) {
					dispatch('GET_EXPORT_STATUS_REQUEST', null, {root:true});
				}
			})
			.catch(error => {
				commit(USER_ERROR);
				// if response is unauthorized, logout
				dispatch('auth/LOGOUT');
			});
	},
	[UPDATE_USER_REQUEST]: ({ commit, dispatch }, payload) => {
		commit(UPDATE_USER_REQUEST);

		return userService.updateUser(payload)
			.then(response => {
				// dispatch(ACCOUNT_REQUEST);
				commit(UPDATE_USER_SUCCESS);
			})
			.catch(error => {
				commit(UPDATE_USER_ERROR);
				// if response is unauthorized, logout
			});
	},
	[CREATE_ADDRESS_REQUEST]: ({ commit, dispatch }, payload) => {
		return userService.createAddress(payload)
			.then(address => {
				// dispatch(ACCOUNT_REQUEST);
				commit(CREATE_ADDRESS_SUCCESS, address);
				return Promise.resolve(address);
			})
			.catch(error => {
				return Promise.reject(error);
			});
	},
	[SET_ADDRESSES]: ({ commit, dispatch }, payload) => {
		dispatch('GET_ESTIMATED_TAX', payload[0].postalCode, {root:true});
		commit(SET_ADDRESSES, payload);
	},
	[GET_EXPORT_STATUS_REQUEST]: ({ commit, rootGetters }) => {
		return userService.getExportStatus()
			.then(status => {
				commit(GET_EXPORT_STATUS_SUCCESS, status.isPending);
			})
			.catch(reason => {
				console.error(reason);
			});
	},
	[SET_EXPORT_FLAG]: ({ commit }, isPending) => {
		commit(GET_EXPORT_STATUS_SUCCESS, isPending);
	},
	[EMAIL_CHANGE_REQUEST]: ({ commit, dispatch }, payload) => {
		return new Promise((resolve, reject) => {
			commit(EMAIL_CHANGE_REQUEST);
			return userService.changeEmail(payload)
				.then(response => {
					commit(EMAIL_CHANGE_SUCCESS, response);
					resolve(response);
				})
				.catch(error => {
					commit(EMAIL_CHANGE_ERROR, error);
					reject(error);
				});
		});
	},
	[CONFIRM_EMAIL_REQUEST]: ({ commit, dispatch }, payload) => {
		return new Promise((resolve, reject) => {
			commit(EMAIL_CHANGE_REQUEST);
			return userService.confirmEmail(payload)
				.then(response => {
					commit(CONFIRM_EMAIL_SUCCESS, response);
					resolve(response);
				})
				.catch(error => {
					commit(CONFIRM_EMAIL_ERROR, error);
					reject(error);
				});
		});
	},
	[PASSWORD_CHANGE_REQUEST]: ({ commit, dispatch }, payload) => {
		return new Promise((resolve, reject) => {
			commit(PASSWORD_CHANGE_REQUEST);

			const password = payload.password,
				newPassword = payload.newPassword;

			// error out before making request
			if (!password || !newPassword || newPassword.length < MIN_PASSWORD_LENGTH) {
				commit(PASSWORD_CHANGE_ERROR, {
					newPasswordRequired: !newPassword,
					newPasswordShort: newPassword.length < MIN_PASSWORD_LENGTH
				});
				reject();
				return;
			}

			return userService.changePassword(payload)
				.then(response => {
					commit(PASSWORD_CHANGE_SUCCESS, response);
					resolve(response);
				})
				.catch(error => {
					commit(PASSWORD_CHANGE_ERROR, error);
					reject(error);
				});
		});
	},
	[CREATE_TFA_REQUEST]: ({ commit, dispatch }) => {
		return new Promise((resolve, reject) => {
			commit(CREATE_TFA_REQUEST);
			return userService.createTfa()
				.then(response => {
					commit(CREATE_TFA_SUCCESS, response);
					resolve(response);
				})
				.catch(error => {
					commit(CREATE_TFA_ERROR, error);
					reject(error);
				});
		});
	},
	[ENABLE_TFA_REQUEST]: ({ commit, dispatch }, payload) => {
		return new Promise((resolve, reject) => {
			commit(ENABLE_TFA_REQUEST);
			return userService.enableTfa(payload)
				.then(response => {
					commit(ENABLE_TFA_SUCCESS, response);
					resolve(response);
				})
				.catch(error => {
					commit(ENABLE_TFA_ERROR, error);
					reject(error);
				});
		});
	},
	[DISABLE_TFA_REQUEST]: ({ commit, dispatch }, payload) => {
		return new Promise((resolve, reject) => {
			commit(DISABLE_TFA_REQUEST);
			return userService.disableTfa(payload)
				.then(response => {
					commit(DISABLE_TFA_SUCCESS);
					resolve(response);
				})
				.catch(error => {
					commit(DISABLE_TFA_ERROR, error);
					reject(error);
				});
		});
	},
	[DELETE_REQUEST]: ({ commit, dispatch }, payload) => {
		return new Promise((resolve, reject) => {
			commit(DELETE_REQUEST);
			return userService.deleteAccount(payload)
				.then(response => {
					resolve(response);
					commit(DELETE_SUCCESS);
				})
				.catch(error => {
					commit(DELETE_ERROR, error);
					reject(error);

				});
		});
	},
	[SET_EMAIL_VERIFIED]: ({ commit, dispatch}, payload) => {
		commit(SET_EMAIL_VERIFIED, payload);
	},
	[RESET_USER_STATUS]: ({ commit, dispatch}) => {
		commit(RESET_USER_STATUS);
	},
};

const mutations = {
	[USER_REQUEST]: (state) => {
		state.status.user.info = loading();
	},
	[USER_SUCCESS]: (state, response) => {
		state.status.user.info = success();
		Vue.set(state, 'user', response);
	},
	[USER_ERROR]: (state, response) => {
		state.status.user.info = error(response);
	},
	[UPDATE_USER_REQUEST]: (state) => {
		state.status.user.update = loading();
	},
	[UPDATE_USER_SUCCESS]: (state) => {
		state.status.user.update = success();
		//TODO: have api return response to change values other than data collection
		state.user.allowDataCollection = !state.user.allowDataCollection;
	},
	[CREATE_ADDRESS_SUCCESS]: (state, response) => {
		state.user.addresses.push(response);
	},
	[SET_ADDRESSES]: (state, addresses) => {
		state.user.addresses = addresses;
		state.user.address = addresses[addresses.length - 1];
	},
	[GET_EXPORT_STATUS_SUCCESS]: (state, isPending) => {
		state.user.exportPending = isPending;
	},
	[SET_EMAIL_VERIFIED]: (state, response) => {
		state.user.emailVerified = response;
	},
	[UPDATE_USER_ERROR]: (state, response) => {
		state.status.user.update = error(response);
	},
	[PASSWORD_CHANGE_REQUEST]: (state) => {
		state.status.user.password = loading();
	},
	[PASSWORD_CHANGE_SUCCESS]: (state, response) => {
		state.status.user.password = success();
	},
	[PASSWORD_CHANGE_ERROR]: (state, response) => {
		state.status.user.password = error(response);
	},
	[EMAIL_CHANGE_ERROR]: (state, response) => {
		state.status.user.email = error(response);
	},
	[EMAIL_CHANGE_REQUEST]: (state) => {
		state.status.user.email = loading();
	},
	[EMAIL_CHANGE_SUCCESS]: (state, response) => {
		state.status.user.email = success();
	},
	[CONFIRM_EMAIL_ERROR]: (state, response) => {
		state.status.user.confirmEmail = error(response);
	},
	[CONFIRM_EMAIL_REQUEST]: (state) => {
		state.status.user.confirmEmail = loading();
	},
	[CONFIRM_EMAIL_SUCCESS]: (state, response) => {
		state.status.user.confirmEmail = success();
	},
	[CREATE_TFA_REQUEST]: (state) => {
		state.status.user.createTfa = loading();
	},
	[CREATE_TFA_SUCCESS]: (state, response) => {
		state.status.user.createTfa = success();
		Vue.set(state, 'tfa', response);
	},
	[CREATE_TFA_ERROR]: (state, response) => {
		state.status.user.createTfa = error(response);
	},
	[ENABLE_TFA_REQUEST]: (state) => {
		state.status.user.enableTfa = loading();
	},
	[ENABLE_TFA_SUCCESS]: (state, response) => {
		state.status.user.enableTfa = success();
		state.user.isMfaEnabled = true;
	},
	[ENABLE_TFA_ERROR]: (state, response) => {
		state.status.user.enableTfa = error(response);
	},
	[DISABLE_TFA_REQUEST]: (state) => {
		state.status.user.disableTfa = loading();
	},
	[DISABLE_TFA_SUCCESS]: (state, response) => {
		state.status.user.disableTfa = success();
		state.user.isMfaEnabled = false;
	},
	[DISABLE_TFA_ERROR]: (state, response) => {
		state.status.user.disableTfa = error(response);
	},
	[DELETE_REQUEST]: (state) => {
		state.status.user.delete = loading();
	},
	[DELETE_SUCCESS]: (state, response) => {
		state.status.user.delete = success();
	},
	[DELETE_ERROR]: (state, response) => {
		state.status.user.delete = error(response);
	},
	[RESET_USER_STATUS]: (state) => {
		const userStatus = state.status.user;
		Object.keys(userStatus).forEach(key => {
			if (key === 'info') {
				return;
			}
			userStatus[key] = {};
		});
	},
	[AUTH_LOGOUT]: (state) => {
		state.user = {};
	}
};

export default {
	state,
	getters,
	actions,
	mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/user.js